import React, { useMemo } from 'react';
import { Row, Col, Table } from 'antd';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import { format2 } from '@/utils/charts';
import type { PvDailyRow } from '@/types/api';

interface RevenuePvTabProps {
  rows: PvDailyRow[];
  monthPdiffMap: Record<string, number>;
  monthMtdMap: Record<string, number>;
  missingDates: string[];
  subsidyPrice: number;
}

const RevenuePvTab: React.FC<RevenuePvTabProps> = ({
  rows,
  monthPdiffMap: _monthPdiffMap,
  monthMtdMap: _monthMtdMap,
  missingDates,
  subsidyPrice: _subsidyPrice,
}) => {
  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalRt = 0;
    let totalSub = 0;
    let totalIp = 0;
    let totalEnergy = 0;
    for (const r of rows) {
      totalRevenue += Number(r['总收益(元)']) || 0;
      totalRt += Number(r['实时电费(元)']) || 0;
      totalSub += Number(r['场外补贴电费(元)']) || 0;
      totalIp += Number(r['省间现货电费(元)']) || 0;
      totalEnergy += Number(r['总上网电量(MWh)']) || 0;
    }
    return { totalRevenue, totalRt, totalSub, totalIp, totalEnergy, avgPrice: totalEnergy ? totalRevenue / totalEnergy : 0 };
  }, [rows]);

  const xLabels = useMemo(() => rows.map((r) => {
    const d = new Date(r['日期'] + 'T00:00:00');
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }), [rows]);

  const revenues = useMemo(() => rows.map((r) => Number(r['总收益(元)']) || 0), [rows]);
  const energies = useMemo(() => rows.map((r) => Number(r['总上网电量(MWh)']) || 0), [rows]);

  const revenueHelpers = useMemo(() => {
    return revenues.map((_, i) => {
      if (i === 0) return 0;
      return revenues.slice(0, i).reduce((a, b) => a + b, 0);
    });
  }, [revenues]);

  const energyHelpers = useMemo(() => {
    return energies.map((_, i) => {
      if (i === 0) return 0;
      return energies.slice(0, i).reduce((a, b) => a + b, 0);
    });
  }, [energies]);

  const revenueWaterfallOption: EChartsOption = useMemo(() => {
    if (!rows.length) return {};
    const categories = [...xLabels, '合计'];
    const helpers = [...revenueHelpers, 0];
    const values = [...revenues, totals.totalRevenue];
    helpers[helpers.length - 1] = 0;
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => {
        const p = params[1] || params[0];
        if (!p) return '';
        const idx = p.dataIndex;
        const val = values[idx];
        const isTotal = idx === values.length - 1;
        return `${categories[idx]}<br/>${isTotal ? '合计' : '收益'}: ${format2(val)} 元`;
      }},
      toolbox: { show: false },
      dataZoom: { show: false },
      grid: { left: 60, right: 30, top: 30, bottom: 60 },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', name: '元', splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        {
          name: '辅助',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: 'transparent' },
          data: helpers,
        },
        {
          name: '收益',
          type: 'bar',
          stack: 'total',
          data: values.map((v, i) => ({
            value: v,
            itemStyle: {
              color: i === values.length - 1 ? '#0d9488' : v >= 0 ? '#0d9488' : '#ef4444',
            },
          })),
          label: { show: true, position: 'top', formatter: (p: any) => format2(p.value) },
        },
      ],
    };
  }, [rows, xLabels, revenueHelpers, revenues, totals.totalRevenue]);

  const energyWaterfallOption: EChartsOption = useMemo(() => {
    if (!rows.length) return {};
    const categories = [...xLabels, '合计'];
    const helpers = [...energyHelpers, 0];
    const values = [...energies, totals.totalEnergy];
    helpers[helpers.length - 1] = 0;
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => {
        const p = params[1] || params[0];
        if (!p) return '';
        const idx = p.dataIndex;
        const val = values[idx];
        const isTotal = idx === values.length - 1;
        return `${categories[idx]}<br/>${isTotal ? '合计' : '电量'}: ${format2(val)} MWh`;
      }},
      toolbox: { show: false },
      dataZoom: { show: false },
      grid: { left: 60, right: 30, top: 30, bottom: 60 },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', name: 'MWh', splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        {
          name: '辅助',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: 'transparent' },
          data: helpers,
        },
        {
          name: '电量',
          type: 'bar',
          stack: 'total',
          data: values.map((v, i) => ({
            value: v,
            itemStyle: {
              color: i === values.length - 1 ? '#0d9488' : v >= 0 ? '#f59e0b' : '#ef4444',
            },
          })),
          label: { show: true, position: 'top', formatter: (p: any) => format2(p.value) },
        },
      ],
    };
  }, [rows, xLabels, energyHelpers, energies, totals.totalEnergy]);

  const tableColumns = [
    { title: '日期', dataIndex: 'date', key: 'date' },
    { title: '实时电费(元)', dataIndex: 'rt', key: 'rt', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '场外补贴电费(元)', dataIndex: 'sub', key: 'sub', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '省间现货电费(元)', dataIndex: 'ip', key: 'ip', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总收益(元)', dataIndex: 'total', key: 'total', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总上网电量(MWh)', dataIndex: 'energy', key: 'energy', align: 'right' as const, render: (v: number) => format2(v) },
  ];

  const tableData = useMemo(() => {
    const data = rows.map((r) => ({
      key: r['日期'],
      date: r['日期'],
      rt: Number(r['实时电费(元)']) || 0,
      sub: Number(r['场外补贴电费(元)']) || 0,
      ip: Number(r['省间现货电费(元)']) || 0,
      total: Number(r['总收益(元)']) || 0,
      energy: Number(r['总上网电量(MWh)']) || 0,
    }));
    const sum = {
      key: 'sum',
      date: '合计',
      rt: data.reduce((s, r) => s + r.rt, 0),
      sub: data.reduce((s, r) => s + r.sub, 0),
      ip: data.reduce((s, r) => s + r.ip, 0),
      total: data.reduce((s, r) => s + r.total, 0),
      energy: data.reduce((s, r) => s + r.energy, 0),
    };
    return [...data, sum];
  }, [rows]);

  return (
    <div>
      {/* KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总收益</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalRevenue)} 元</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总上网电量</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalEnergy)} MWh</div>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>综合电价</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.avgPrice)} 元/MWh</div>
          </div>
        </Col>
      </Row>

      {/* Daily table */}
      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>按日收益汇总表</div>
      <Table
        columns={tableColumns}
        dataSource={tableData}
        pagination={false}
        size="small"
        bordered
      />

      {/* Waterfall charts */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <ChartCard title="收益瀑布图" option={revenueWaterfallOption} height={450} />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="电量瀑布图" option={energyWaterfallOption} height={450} />
        </Col>
      </Row>

      {missingDates.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
          以下日期暂无数据，已跳过：{missingDates.join('，')}
        </div>
      )}
    </div>
  );
};

export default RevenuePvTab;
